import * as React from 'react';
import styles from './use_styles';
import useDirection from '../direction/use_direction';
import { NotificationBadgeInterface } from './types';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { useTheme } from '@veneer/theme';

const NotificationBadge = React.forwardRef<
  HTMLElement,
  NotificationBadgeInterface &
    Omit<React.HTMLAttributes<HTMLElement>, keyof NotificationBadgeInterface>
>(
  (
    {
      children,
      component: Component = 'span',
      position: positionProp = 'top-end',
      count,
      maxCount = 100,
      ...other
    },
    ref
  ) => {
    const { isRTL } = useDirection();
    const theme = useTheme();
    const isHc = theme.mode === 'contrast';

    const [verticalPosition, horizontalPosition] = positionProp.split('-');

    const mapHorizontalPosition: Record<string, string> = {
      left: isRTL ? 'right' : 'left',
      start: 'left',
      right: isRTL ? 'left' : 'right',
      end: 'right',
    };

    const style = styles({
      horizontal: mapHorizontalPosition[horizontalPosition],
      vertical: verticalPosition,
    });

    if (count || count === 0) {
      const finalCount = Number(count >= maxCount)
        ? `${Number(maxCount) - 1}+`
        : count;

      return (
        <Component
          {...other}
          css={[style.root, highContrastMediaQuery(theme)]}
          ref={ref}
        >
          {children}
          <span
            css={[
              children && style.count.floating,
              style.count.base,
              isHc && style.count.hc,
            ]}
          >
            {finalCount}
          </span>
        </Component>
      );
    }

    return (
      <Component
        {...other}
        css={[style.root, highContrastMediaQuery(theme)]}
        ref={ref}
      >
        {children}
        <span css={style.badge} />
      </Component>
    );
  }
);

NotificationBadge.displayName = 'NotificationBadge';

export default NotificationBadge;
